import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Job } from '../types';
import Layout from '../components/Layout';
import { MdWork, MdPerson, MdCheckCircle } from 'react-icons/md';
import { HiCalendar, HiFolder, HiCloudArrowUp } from 'react-icons/hi2';

const Jobs: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await apiService.getJobs();
      if (response && response.success && response.data) {
        setJobs(response.data.results);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleViewAsUser = async (job: Job) => {
    try {
      // Get impersonation token for the job owner
      const response = await apiService.impersonateUser(job.owner);
      
      if (response.success) {
        const { access_token, refresh_token, admin_id, admin_email } = response.data;
        
        // Store impersonation data in URL parameters
        const impersonationData = {
          access: access_token,
          refresh: refresh_token,
          adminId: admin_id,
          adminEmail: admin_email,
          adminReturn: window.location.href, // Store current URL to return to
        };
        
        // Encode data as base64 for URL
        const encodedData = btoa(JSON.stringify(impersonationData));
        
        // Redirect to FrontEnd with impersonation token
        const frontendUrl = `https://app.teamther.ai/job-details/${job.id}?impersonate=${encodedData}`;
        window.location.href = frontendUrl;
      }
    } catch (error: any) {
      alert('Error impersonating user: ' + error.message);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.owner_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };


  // Stats calculations
  const stats = {
    totalJobs: jobs.length,
    activeJobs: jobs.filter(j => j.status === 'active').length,
    totalCVs: jobs.reduce((sum, job) => sum + (job.cv_count || 0), 0),
    analyzedCVs: jobs.reduce((sum, job) => sum + (job.analyzed_count || 0), 0),
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-screen items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-navy-700 dark:text-white">Jobs Management</h1>
          
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">Total Jobs</p>
              <p className="mt-2 text-3xl font-bold">{stats.totalJobs}</p>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <MdWork className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-gradient-to-br from-green-500 to-green-600 p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">Active Jobs</p>
              <p className="mt-2 text-3xl font-bold">{stats.activeJobs}</p>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <MdCheckCircle className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">Total CVs</p>
              <p className="mt-2 text-3xl font-bold">{stats.totalCVs}</p>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <HiCloudArrowUp className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">Analyzed CVs</p>
              <p className="mt-2 text-3xl font-bold">{stats.analyzedCVs}</p>
            </div>
            <div className="rounded-full bg-white/20 p-3">
              <HiFolder className="h-8 w-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by job title or owner..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
          />
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="overflow-hidden rounded-xl bg-white shadow-lg dark:bg-navy-800">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-navy-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Job Title
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Created By
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  CVs Uploaded
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Analyzed
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Not CV
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Total Size
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Created
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Last Analysis
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Data Retention
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-navy-600">
              {filteredJobs.map((job) => (
                <tr
                  key={job.id}
                  onClick={() => handleViewAsUser(job)}
                  className="hover:bg-gray-50 dark:hover:bg-navy-700/50 transition cursor-pointer"
                  title="Click to view as user"
                >
                  <td className="px-6 py-4">
                    <p className="font-medium text-navy-700 dark:text-white">{job.title}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <MdPerson className="mr-2 h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-navy-700 dark:text-white">{job.owner_name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{job.owner_email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      {job.cv_count || 0} CVs
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      {job.analyzed_count || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                      {job.not_cv_count || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {formatBytes(job.total_size || 0)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {formatDate(job.created_at)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {formatDateTime(job.last_analysis_date)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {job.date_data_retention ? (
                      <span className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-sm font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                        {new Date(job.date_data_retention).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredJobs.length === 0 && (
            <div className="py-12 text-center">
              <MdWork className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">No jobs found</p>
            </div>
          )}
        </div>
      </div>
      </div>
    </Layout>
  );
};

export default Jobs;
